import { useEffect, useState, useCallback } from 'react';

const KEY = 'am_favorites_v1';
const EVENT = 'am-favorites-changed';

/** localStorage-backed favourites list. Cross-component sync via a
 *  custom `am-favorites-changed` window event so any mounted component
 *  reflects an add/remove instantly (no reload). */
export function useFavorites() {
  const [ids, setIds] = useState(() => {
    if (typeof window === 'undefined') return [];
    try {
      const raw = window.localStorage.getItem(KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    const sync = () => {
      try {
        const raw = window.localStorage.getItem(KEY);
        setIds(raw ? JSON.parse(raw) : []);
      } catch {
        setIds([]);
      }
    };
    window.addEventListener(EVENT, sync);
    window.addEventListener('storage', sync);   // sync across tabs
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const persist = useCallback((next) => {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(next));
    } catch { /* ignore quota errors */ }
    window.dispatchEvent(new CustomEvent(EVENT));
  }, []);

  const isFav = useCallback((id) => ids.includes(id), [ids]);

  const toggle = useCallback((id) => {
    if (!id) return;
    const next = ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id];
    setIds(next);
    persist(next);
  }, [ids, persist]);

  const clear = useCallback(() => {
    setIds([]);
    persist([]);
  }, [persist]);

  // Merge in additional IDs (e.g. from a shared ?fav=... URL) without
  // clobbering the user's existing list. Deduplicates + keeps existing order.
  const mergeIn = useCallback((incoming) => {
    if (!Array.isArray(incoming) || incoming.length === 0) return;
    const set = new Set(ids);
    const additions = incoming.filter((x) => x && !set.has(x));
    if (!additions.length) return;
    const next = [...ids, ...additions];
    setIds(next);
    persist(next);
  }, [ids, persist]);

  return { favIds: ids, isFav, toggle, clear, mergeIn, count: ids.length };
}
